import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/keys';
import { setSessionUser } from './session';

export interface SignOutControls {
  /** End this device's session, forget the signed-in user's data, and go to `redirectTo`. */
  signOut: () => Promise<void>;
  /** The request is in flight (disable the button). */
  pending: boolean;
}

/**
 * Sign out of this device (`POST /auth/logout`). The browser is signed out whatever the server says:
 * if the request fails (offline, session already gone) the local session, the cached account and
 * back-office data and the "a session exists" hint are dropped anyway, so a failed call can never
 * leave someone looking at another person's data on a shared computer.
 */
export function useSignOut(redirectTo = '/'): SignOutControls {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);

  const signOut = useCallback(async (): Promise<void> => {
    setPending(true);
    try {
      await apiFetch<void>('/auth/logout', { method: 'POST' });
    } catch {
      // Signed out locally regardless — see above.
    } finally {
      setSessionUser(null);
      for (const queryKey of [
        queryKeys.me.all,
        queryKeys.auth.all,
        queryKeys.admin.all,
        queryKeys.super.all,
      ]) {
        queryClient.removeQueries({ queryKey });
      }
      setPending(false);
      void navigate(redirectTo, { replace: true });
    }
  }, [navigate, queryClient, redirectTo]);

  return { signOut, pending };
}
